import type { IdeateRequest, ParsedIdea } from '@malaberg/shared';
/**
 * useIdeate hook — T7.
 *
 * Wraps ideationApi.suggest with a state machine, abort-on-resubmit semantics,
 * and stable callback references via useCallback.
 *
 * State machine:
 *   idle ──suggest()──► loading ──success──► success
 *                                 └──error───► error
 *   (any state) ──reset()──► idle
 *   (any state) ──suggest()──► loading   (previous in-flight call is aborted)
 *
 * The hook does NOT show toasts or notifications. The UI layer (T8 component)
 * is responsible for rendering state-derived feedback.
 */
import { useCallback, useRef, useState } from 'react';
import { ideationApi } from '../api/endpoints/ideation';
import { ApiError } from '../api/errors';

// ─── Types ────────────────────────────────────────────────────────────────────

type UseIdeateState =
  | { status: 'idle'; ideas: null; error: null }
  | { status: 'loading'; ideas: null; error: null }
  | { status: 'success'; ideas: ParsedIdea[]; error: null }
  | { status: 'error'; ideas: null; error: { message: string; code?: string } };

export type UseIdeateResult = UseIdeateState & {
  /**
   * Submit an ideation request. If a previous call is in flight it is aborted
   * (latest-wins). Transitions to loading immediately, then to success or error.
   */
  suggest: (request: IdeateRequest) => Promise<void>;
  /**
   * Abort any in-flight request and return to idle state.
   */
  reset: () => void;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useIdeate(): UseIdeateResult {
  const [state, setState] = useState<UseIdeateState>({
    status: 'idle',
    ideas: null,
    error: null,
  });

  // AbortController for the current in-flight request. Mutated via ref so
  // useCallback closures always read the latest controller without stale captures.
  const abortRef = useRef<AbortController | null>(null);

  const suggest = useCallback(async (request: IdeateRequest): Promise<void> => {
    // Abort any previously in-flight request (latest-wins semantics)
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setState({ status: 'loading', ideas: null, error: null });

    try {
      const { ideas } = await ideationApi.suggest(request, { signal: ctrl.signal });

      // Guard: if this call was superseded by a newer suggest() or reset(),
      // the signal is already aborted — discard the stale result silently.
      if (ctrl.signal.aborted) return;

      setState({ status: 'success', ideas, error: null });
    } catch (err) {
      // AbortError means reset() or a subsequent suggest() cancelled this call —
      // do not update state (would cause flicker / overwrite the newer state).
      if (ctrl.signal.aborted || (err as Error).name === 'AbortError') return;

      const message =
        err instanceof ApiError ? err.message : ((err as Error).message ?? 'Unknown error');
      const code = err instanceof ApiError ? err.code : undefined;

      setState({ status: 'error', ideas: null, error: { message, code } });
    }
  }, []);

  const reset = useCallback((): void => {
    abortRef.current?.abort();
    setState({ status: 'idle', ideas: null, error: null });
  }, []);

  return { ...state, suggest, reset };
}
